import React, { useState, useEffect, useMemo } from "react"
import { Box, Button, TextField, Select, MenuItem, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress } from '@material-ui/core'
import ViewHeadlineIcon from '@material-ui/icons/ViewHeadline'
import { useStyles } from './styles'
import { ACTIVE_SITES_DEFAULT_BODY, ACTIVE_SITES_VIEW_MODES, ACTIVE_SITES_VIEW_LABELS, buildActiveSitesHeadings, ACTIVE_SITES_EMPTY_TABLE_TEXT, getErrorMessage } from './constants'
import TableData from './TableData'
import Search from '../../../elements/icons/Search'
import Download from '../../../elements/icons/Download'
import { DisplayMessage } from '../../../Components'
import { Loader } from '../../../Components'
import { eventService } from '../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../utils/messageTypes'
import DraftsIcon from '@material-ui/icons/Drafts'

const ActiveSites = (props) => {
  const classes = useStyles()

  const [reqBody, setReqBody] = useState(ACTIVE_SITES_DEFAULT_BODY)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  const [activeSitesData, setActiveSitesData] = useState([])
  const [maxMonthlyData, setMaxMonthlyData] = useState([])
  const [activeSitesDateKeys, setActiveSitesDateKeys] = useState([])
  const [maxMonthlyDateKeys, setMaxMonthlyDateKeys] = useState([])
  const [viewMode, setViewMode] = useState(ACTIVE_SITES_VIEW_MODES.ACTIVE_SITES)
  const [searchTerm, setSearchTerm] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [accounts, setAccounts] = useState([])
  const [hideZero, setHideZero] = useState(false)
  const [groupByAccount, setGroupByAccount] = useState(false)
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState(1)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  // Drill-down state: keyed by "programId__dateKey"
  const [expandedDrillDown, setExpandedDrillDown] = useState(null)
  const [drillDownCache, setDrillDownCache] = useState({})
  const [drillDownLoading, setDrillDownLoading] = useState({})

  const isActiveSites = viewMode === ACTIVE_SITES_VIEW_MODES.ACTIVE_SITES
  const allData = isActiveSites ? activeSitesData : maxMonthlyData
  const dateKeys = isActiveSites ? activeSitesDateKeys : maxMonthlyDateKeys

  useEffect(() => {
    setPage(0)
  }, [viewMode, searchTerm, accountFilter, hideZero, sortCol, sortDir])

  useEffect(() => {
    fetchAndRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const normalizeDataForKey = (data, dataKey) => {
    const keySet = new Set()
    data.forEach(row => (row[dataKey] || []).forEach(obj => Object.keys(obj).forEach(dateFrame => keySet.add(dateFrame))))
    const dateKeysArray = Array.from(keySet).sort()

    const normalizedData = data.map(row => {
      const counts = {};
      (row[dataKey] || []).forEach(obj => Object.assign(counts, obj))
      return { ...row, counts }
    })

    return { normalizedData, dateKeysArray }
  }

  const processResponse = (json) => {
    const data = json.data
    if (!Array.isArray(data) || data.length === 0) {
      setMessage('No data array found in response')
      setMessageType('error')
      setOpen(true)
      setActiveSitesData([])
      setMaxMonthlyData([])
      setActiveSitesDateKeys([])
      setMaxMonthlyDateKeys([])
      setAccounts([])
      return
    }

    const active = normalizeDataForKey(data, ACTIVE_SITES_VIEW_MODES.ACTIVE_SITES)
    setActiveSitesData(active.normalizedData)
    setActiveSitesDateKeys(active.dateKeysArray)

    const maxMonthly = normalizeDataForKey(data, ACTIVE_SITES_VIEW_MODES.MAX_MONTHLY)
    setMaxMonthlyData(maxMonthly.normalizedData)
    setMaxMonthlyDateKeys(maxMonthly.dateKeysArray)

    const uniqueAccounts = [...new Set(active.normalizedData.map(r => r.accountName))].sort()
    setAccounts(uniqueAccounts)

    setSortCol(null)
    setSortDir(1)

    setMessage(`Loaded ${active.normalizedData.length} program(s)`)
    setMessageType('success')
    setOpen(true)
  }

  const fetchAndRender = async () => {
    let body = undefined
    try {
      const trimmedReqBody = reqBody.trim()
      body = JSON.parse(trimmedReqBody)
    } catch (err) {
      setMessage('Invalid JSON in Request Body')
      setMessageType('error')
      setOpen(true)
      return
    }

    setLoading(true)
    eventService.getActiveSites(body, props.currentUser.account_id)
      .then(data => processResponse(data))
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load Active Sites'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const getTrendValue = (row) => {
    if (dateKeys.length < 2) return 0
    const last = row.counts[dateKeys[dateKeys.length - 1]] ?? 0
    const prev = row.counts[dateKeys[dateKeys.length - 2]] ?? 0
    return last - prev
  }

  // Drill-down: toggle expanded row and fetch history
  const getDrillDownKey = (programId, dateKey) => `${programId}__${dateKey}`

  const handleDrillDown = (item, dateKey) => {
    const key = getDrillDownKey(item.programId, dateKey)

    // Toggle: if same key is already expanded, collapse it
    if (expandedDrillDown === key) {
      setExpandedDrillDown(null)
      return
    }

    setExpandedDrillDown(key)

    // If already cached, no need to fetch
    if (drillDownCache[key]) return

    setDrillDownLoading(prev => ({ ...prev, [key]: true }))

    const params = { program_id: item.programId }
    if (isActiveSites) {
      params.date_key = dateKey
    } else {
      params.month_key_for_max_active = dateKey
    }

    eventService.getActiveSitesHistory(props.currentUser.account_id, params)
      .then(response => {
        setDrillDownCache(prev => ({ ...prev, [key]: response.data || response }))
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load drill-down data'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
        setExpandedDrillDown(null)
      })
      .finally(() => {
        setDrillDownLoading(prev => ({ ...prev, [key]: false }))
      })
  }

  const exportDrillDownCsv = (type, drillData, programName, dateKey) => {
    if (!drillData) return
    const list = type === 'sites' ? (drillData.sites || []) : (drillData.sitesWithoutBattery || [])
    if (list.length === 0) return

    const headers = ['Sr No', 'Site ID']
    const rows = list.map((siteId, index) => `${index + 1},${siteId}`)
    const csvContent = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = downloadUrl
    const label = type === 'sites' ? 'active-sites' : 'sites-without-battery'
    downloadLink.download = `${label}-${programName || 'program'}-${dateKey || ''}.csv`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  }

  const { filteredData, stats } = useMemo(() => {
    if (allData.length === 0) {
      return { filteredData: [], stats: null }
    }

    let filtered = allData.filter(row => {
      if (accountFilter && row.accountName !== accountFilter) return false
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        if (!row.programNameAdminUI?.toLowerCase().includes(search) &&
          !row.accountName?.toLowerCase().includes(search) &&
          !row.programKey?.toLowerCase().includes(search)) {
          return false
        }
      }
      if (hideZero && dateKeys.every(k => (row.counts[k] ?? 0) === 0)) return false
      return true
    })

    if (sortCol !== null) {
      filtered = [...filtered].sort((a, b) => {
        let aVal, bVal
        if (sortCol === 'program') {
          aVal = (a.programNameAdminUI || '').toLowerCase()
          bVal = (b.programNameAdminUI || '').toLowerCase()
        } else if (sortCol === 'account') {
          aVal = (a.accountName || '').toLowerCase()
          bVal = (b.accountName || '').toLowerCase()
        } else if (sortCol === 'trend') {
          aVal = getTrendValue(a)
          bVal = getTrendValue(b)
        } else {
          aVal = a.counts[sortCol] ?? 0
          bVal = b.counts[sortCol] ?? 0
        }
        if (aVal < bVal) return -sortDir
        if (aVal > bVal) return sortDir
        return 0
      })
    }

    let computedStats = null
    if (dateKeys.length > 0) {
      const lastKey = dateKeys[dateKeys.length - 1]
      const prevKey = dateKeys[dateKeys.length - 2]
      const total = filtered.reduce((sum, row) => sum + (row.counts[lastKey] ?? 0), 0)
      const totalPrev = filtered.reduce((sum, row) => sum + (row.counts[prevKey] ?? 0), 0)
      const nonZero = filtered.filter(row => dateKeys.some(k => (row.counts[k] ?? 0) > 0)).length
      const delta = total - totalPrev

      computedStats = {
        programCount: filtered.length,
        nonZero,
        total,
        lastKey,
        prevKey,
        delta,
        uniqueAccounts: new Set(filtered.map(r => r.accountName)).size,
        dateRange: `${dateKeys[0]} – ${lastKey}`,
        monthCount: dateKeys.length,
      }
    }

    return { filteredData: filtered, stats: computedStats }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, searchTerm, accountFilter, hideZero, sortCol, sortDir, dateKeys])

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 1 ? -1 : 1)
    } else {
      setSortCol(col)
      setSortDir(1)
    }
  }

  const exportCsv = () => {
    if (filteredData.length === 0) return

    const headers = ['Sr No', 'Program Name', 'Account', 'Operated By', ...dateKeys, 'Trend']

    const escapeCsvValue = (value) => {
      const stringValue = value == null ? '' : String(value)
      return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue
    }

    const rows = filteredData.map((row, index) => {
      const dateCols = dateKeys.map(dateKey => row.counts[dateKey] ?? 0)
      const trend = getTrendValue(row)
      return [
        index + 1,
        row.programNameAdminUI,
        row.accountName,
        row.operatedBy || '',
        ...dateCols,
        trend
      ].map(escapeCsvValue).join(',')
    })

    const csvContent = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = downloadUrl
    const filePrefix = isActiveSites ? 'active-sites' : 'max-monthly-active-sites'
    downloadLink.download = `${filePrefix}-${new Date().toISOString().split('T')[0]}.csv`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  }

  const getCellClass = (value, maxValue) => {
    if (value === 0) return classes.activeSitesCellZero
    const ratio = value / maxValue
    if (ratio < 0.05) return classes.activeSitesCellLow
    if (ratio < 0.3) return classes.activeSitesCellMid
    if (ratio < 0.7) return classes.activeSitesCellHigh
    return classes.activeSitesCellPeak
  }

  const renderTable = () => {

    if (filteredData.length === 0) {
      return (
        <Box className={classes.activeSitesEmptyState}>
          <DraftsIcon fontSize="large" color="disabled" />
          <h3>No results</h3>
          <p>Adjust search or filters.</p>
        </Box>
      )
    }

    const colMax = {}
    dateKeys.forEach(dateKey => {
      colMax[dateKey] = Math.max(...filteredData.map(r => r.counts[dateKey] ?? 0), 1)
    })

    const tableHeadings = buildActiveSitesHeadings(dateKeys, sortCol, sortDir)

    let tableData = []
    let displayIndex = 0

    if (groupByAccount) {
      const groups = {}
      filteredData.forEach(row => {
        if (!groups[row.accountName]) groups[row.accountName] = []
        groups[row.accountName].push(row)
      })

      Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).forEach(([account, grpRows]) => {
        const groupTotals = {}
        dateKeys.forEach(dateKey => {
          groupTotals[dateKey] = grpRows.reduce((sum, row) => sum + (row.counts[dateKey] ?? 0), 0)
        })

        tableData.push({ isGroup: true, account, rowCount: grpRows.length, groupTotals })
        grpRows.forEach(row => {
          displayIndex++
          tableData.push({ ...row, isGroup: false, displayIndex })
        })
      })
    } else {
      tableData = filteredData.map((row, index) => ({
        ...row,
        displayIndex: index + 1,
        isGroup: false
      }))
    }

    // Add totals row
    const totals = {}
    dateKeys.forEach(dateKey => {
      totals[dateKey] = filteredData.reduce((sum, row) => sum + (row.counts[dateKey] ?? 0), 0)
    })
    tableData.push({
      isTotals: true,
      rowCount: filteredData.length,
      totals,
    })

    const getCellClassName = (item, heading) => {
      const baseClass = classes.titleWrapper

      // Group rows
      if (item.isGroup) {
        return classes.activeSitesGroupRow
      }

      // Totals row
      if (item.isTotals) {
        const totalsClasses = [baseClass, classes.activeSitesTotalsRow]
        if (heading.id === 'operatedBy') {
          totalsClasses.push(classes.activeSitesTableCellRight)
        } else if (dateKeys.includes(heading.id)) {
          totalsClasses.push(classes.activeSitesNum)
        }
        return totalsClasses.join(' ')
      }

      // Regular data rows
      const cellClasses = [baseClass]

      if (heading.id === 'index') {
        cellClasses.push(classes.activeSitesTableCellRight, classes.activeSitesIndexCell)
      } else if (dateKeys.includes(heading.id)) {
        const value = item.counts[heading.id] ?? 0
        const heatMapClass = getCellClass(value, colMax[heading.id])
        cellClasses.push(classes.activeSitesNum, heatMapClass)
      } else if (heading.id === 'trend') {
        cellClasses.push(classes.activeSitesTableCellCenter)
      }

      return cellClasses.join(' ')
    }

    const renderCellContent = (item, heading) => {
      if (item.isGroup) {
        if (heading.id === 'index') {
          return `${item.account} (${item.rowCount} programs)`
        }
        if (dateKeys.includes(heading.id)) {
          return item.groupTotals[heading.id].toLocaleString()
        }
        return '—'
      }

      if (item.isTotals) {
        if (heading.id === 'operatedBy') {
          return `Total (${item.rowCount} rows)`
        }
        if (dateKeys.includes(heading.id)) {
          return item.totals[heading.id].toLocaleString()
        }
        return ''
      }

      switch (heading.id) {
        case 'index':
          return item.displayIndex

        case 'program':
          return (
            <Box>
              <Box className={classes.activeSitesProgramName}>
                {item.programNameAdminUI}
                {!isActiveSites && (
                  <Box component="span" className={classes.drillDownMaxMonthlyLabel}>Max Monthly</Box>
                )}
              </Box>
              <Box className={classes.activeSitesProgramKey}>{item.programKey}</Box>
            </Box>
          )

        case 'account':
          return item.accountName

        case 'operatedBy':
          return item.operatedBy || '—'

        case 'trend':
          const trend = getTrendValue(item)
          if (trend === 0) return '—'
          return (
            <Box component="span" className={trend > 0 ? classes.trendPositive : classes.trendNegative}>
              {trend > 0 ? `▲ +${trend}` : `▼ ${trend}`}
            </Box>
          )

        default:
          if (dateKeys.includes(heading.id)) {
            const value = item.counts[heading.id] ?? 0
            if (value === 0) {
              return (
                <Box className={classes.drillDownCellWrapper}>
                  <Box component="span" className={classes.activeSitesZeroValue}>0</Box>
                  <Box component="span" className={classes.drillDownChipPlaceholder} />
                </Box>
              )
            }
            return (
              <Box className={classes.drillDownCellWrapper}>
                <Box component="span">{value.toLocaleString()}</Box>
                <Box
                  component="span"
                  className={classes.drillDownChip}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDrillDown(item, heading.id)
                  }}
                >
                  <ViewHeadlineIcon className={classes.drillDownChipIcon} />
                </Box>
              </Box>
            )
          }
          return item[heading.id] ?? '—'
      }
    }

    const renderExpandedRow = (item, index, colSpan) => {
      if (item.isGroup || item.isTotals || !item.programId) return null
      const key = getDrillDownKey(item.programId, expandedDrillDown?.split('__')[1])
      if (expandedDrillDown !== key && !Object.keys(drillDownCache).some(k => k.startsWith(`${item.programId}__`) && expandedDrillDown === k)) return null

      // Find the active expanded key for this row
      const activeKey = Object.keys(drillDownCache).find(k => k.startsWith(`${item.programId}__`) && expandedDrillDown === k)
        || (expandedDrillDown?.startsWith(`${item.programId}__`) ? expandedDrillDown : null)
      if (!activeKey) return null

      const dateKey = activeKey.split('__')[1]
      const isLoading = drillDownLoading[activeKey]
      const drillData = drillDownCache[activeKey]

      return (
        <TableRow key={`expand-${activeKey}`} className={classes.expandRow}>
          <TableCell colSpan={colSpan} className={classes.drillDownExpandCell}>
            <Box className={classes.drillDownExpandInner}>
              <Box className={classes.drillDownHeader}>
                <Box className={classes.drillDownHeaderTitle}>
                  MONTHLY CHANGE — {dateKey}
                </Box>
                {drillData?.monthlyChangeCount && (
                  <Box className={classes.drillDownChangeCountBar}>
                    <Box component="span" className={classes.drillDownChangeAdded}>
                      ▲ Added: {drillData.monthlyChangeCount.added}
                    </Box>
                    <Box component="span" className={classes.drillDownChangeDeleted}>
                      ▼ Deleted: {drillData.monthlyChangeCount.deleted}
                    </Box>
                  </Box>
                )}
              </Box>

              {isLoading && (
                <Box className={classes.drillDownLoadingContainer}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoading && drillData && (
                <Box className={classes.drillDownTablesContainer}>
                  <Box className={classes.drillDownTableSection}>
                    <Box className={classes.drillDownSectionHeader}>
                      <Box className={classes.drillDownSectionTitle}>
                        ACTIVE SITES ({(drillData.sites || []).length})
                      </Box>
                      <Button
                        className={classes.drillDownExportBtn}
                        onClick={() => exportDrillDownCsv('sites', drillData, item.programNameAdminUI, dateKey)}
                        disabled={(drillData.sites || []).length === 0}
                      >
                        Export CSV
                      </Button>
                    </Box>
                    {(drillData.sites || []).length > 0 ? (
                      <Box className={classes.drillDownNestedTableWrap}>
                        <Table size="small" className={classes.drillDownNestedTable}>
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.drillDownNestedTh}>#</TableCell>
                              <TableCell className={classes.drillDownNestedTh}>SITE ID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(drillData.sites || []).map((siteId, idx) => (
                              <TableRow key={`site-${siteId}-${idx}`}>
                                <TableCell className={classes.drillDownNestedTd}>{idx + 1}</TableCell>
                                <TableCell className={classes.drillDownNestedTd}>{siteId}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : (
                      <Box className={classes.drillDownEmpty}>No sites</Box>
                    )}
                  </Box>

                  <Box className={classes.drillDownTableSection}>
                    <Box className={classes.drillDownSectionHeader}>
                      <Box className={classes.drillDownSectionTitle}>
                        SITES WITHOUT BATTERY ({(drillData.sitesWithoutBattery || []).length})
                      </Box>
                      <Button
                        className={classes.drillDownExportBtn}
                        onClick={() => exportDrillDownCsv('sitesWithoutBattery', drillData, item.programNameAdminUI, dateKey)}
                        disabled={(drillData.sitesWithoutBattery || []).length === 0}
                      >
                        Export CSV
                      </Button>
                    </Box>
                    {(drillData.sitesWithoutBattery || []).length > 0 ? (
                      <Box className={classes.drillDownNestedTableWrap}>
                        <Table size="small" className={classes.drillDownNestedTable}>
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.drillDownNestedTh}>#</TableCell>
                              <TableCell className={classes.drillDownNestedTh}>SITE ID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(drillData.sitesWithoutBattery || []).map((siteId, idx) => (
                              <TableRow key={`nob-${siteId}-${idx}`}>
                                <TableCell className={classes.drillDownNestedTd}>{idx + 1}</TableCell>
                                <TableCell className={classes.drillDownNestedTd}>{siteId}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : (
                      <Box className={classes.drillDownEmpty}>No sites</Box>
                    )}
                  </Box>
                </Box>
              )}

              {!isLoading && !drillData && (
                <Box className={classes.drillDownEmpty}>No data available</Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <TableData
        classes={classes}
        tableHeadings={tableHeadings}
        tableContents={tableData}
        isBold={sortCol}
        handleSortChange={handleSort}
        renderCellContent={renderCellContent}
        getCellClassName={getCellClassName}
        renderExpandedRow={renderExpandedRow}
        containerClassName={classes.tableWrap}
        tableClassName={classes.activeSitesTable}
        stickyHeader={false}
        emptyTableText={ACTIVE_SITES_EMPTY_TABLE_TEXT}
        paginate
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    )
  }

  return (
    <Box>
      <DisplayMessage
        open={open}
        message={message}
        messageType={messageType}
        handleClose={() => setOpen(false)}
      />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlExtraGroup}>
          <Box className={classes.urlBarLabel}>
            <Box>Request Body <Box component="span" className={classes.labelSubtext}>(JSON object)</Box></Box>
            <Button className={classes.submitBtn} onClick={fetchAndRender} disabled={loading}>
              <Search />
              <Box paddingLeft='10px'>Fetch & Render</Box>
            </Button>
          </Box>
          <TextField
            className={classes.reqTextarea}
            value={reqBody}
            onChange={(e) => setReqBody(e.target.value)}
            multiline
            variant="standard"
            InputProps={{
              disableUnderline: true,
              className: classes.activeSitesReqBodyInput,
            }}
          />
        </Box>
      </Box>

      {stats && (
        <Box className={classes.container}>
          <Box className={classes.activeSitesStatsBar}>
            <Box className={classes.activeSitesStatChip}>
              <Box className={classes.activeSitesStatVal}>{stats.programCount}</Box>
              <Box className={classes.activeSitesStatLbl}>Programs</Box>
              <Box className={classes.activeSitesStatSub}>{stats.nonZero} with active sites</Box>
            </Box>
            <Box className={classes.activeSitesStatChip}>
              <Box className={classes.activeSitesStatVal}>{stats.total.toLocaleString()}</Box>
              <Box className={classes.activeSitesStatLbl}>
                {isActiveSites ? 'Active Sites' : 'Max Monthly'} ({stats.lastKey})
              </Box>
              <Box className={stats.delta > 0 ? classes.activeSitesStatSubPositive : stats.delta < 0 ? classes.activeSitesStatSubNegative : classes.activeSitesStatSubNeutral}>
                {stats.delta === 0 ? '—' : (stats.delta > 0 ? `▲ +${stats.delta.toLocaleString()}` : `▼ ${stats.delta.toLocaleString()}`)} vs {stats.prevKey}
              </Box>
            </Box>
            <Box className={classes.activeSitesStatChip}>
              <Box className={classes.activeSitesStatVal}>{stats.uniqueAccounts}</Box>
              <Box className={classes.activeSitesStatLbl}>Unique Accounts</Box>
            </Box>
            <Box className={classes.activeSitesStatChip}>
              <Box className={classes.activeSitesStatValLarge}>{stats.dateRange}</Box>
              <Box className={classes.activeSitesStatLbl}>Date Range</Box>
              <Box className={classes.activeSitesStatSub}>{stats.monthCount} months</Box>
            </Box>
          </Box>

          <Box className={classes.filterBar}>
            <TextField
              className={classes.filterInput}
              placeholder="Search program or account…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Select
              className={classes.filterSelect}
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={{
                classes: { paper: classes.selectPaper },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="">All Accounts</MenuItem>
              {accounts.map(account => (
                <MenuItem key={account} value={account}>{account}</MenuItem>
              ))}
            </Select>
            <Box className={classes.activeSitesViewToggleGroup}>
              <Button
                className={`${classes.activeSitesViewToggleBtn} ${isActiveSites ? classes.activeSitesViewToggleBtnActive : ''}`}
                onClick={() => setViewMode(ACTIVE_SITES_VIEW_MODES.ACTIVE_SITES)}
              >
                {ACTIVE_SITES_VIEW_LABELS[ACTIVE_SITES_VIEW_MODES.ACTIVE_SITES]}
              </Button>
              <Button
                className={`${classes.activeSitesViewToggleBtn} ${classes.activeSitesViewToggleBtnBorder} ${!isActiveSites ? classes.activeSitesViewToggleBtnActive : ''}`}
                onClick={() => setViewMode(ACTIVE_SITES_VIEW_MODES.MAX_MONTHLY)}
              >
                {ACTIVE_SITES_VIEW_LABELS[ACTIVE_SITES_VIEW_MODES.MAX_MONTHLY]}
              </Button>
            </Box>
            <Box className={classes.activeSitesFilterContainer}>
              <Button
                className={`${classes.activeSitesToggleBtn} ${hideZero ? classes.activeSitesToggleBtnActive : ''}`}
                onClick={() => setHideZero(!hideZero)}
              >
                Hide Zero-Only
              </Button>
              <Button
                className={`${classes.activeSitesToggleBtn} ${groupByAccount ? classes.activeSitesToggleBtnActive : ''}`}
                onClick={() => setGroupByAccount(!groupByAccount)}
              >
                Group by Account
              </Button>
              <Button className={classes.exportBtn} onClick={exportCsv}>
                <Download />
                <Box paddingLeft='10px'>{isActiveSites ? 'Export CSV' : 'Export Max CSV'}</Box>
              </Button>
            </Box>
          </Box>

          <Box className={classes.card}>
            {renderTable()}
          </Box>
        </Box>
      )}

      {!stats && activeSitesData.length === 0 && (
        <Box className={classes.container}>
          <Box className={classes.card}>
            <Box className={classes.activeSitesEmptyState}>
              <DraftsIcon fontSize="large" color="disabled" />
              <h3>No Data Found</h3>
              <p>Fetch from a URL or paste JSON to render the active site counts.</p>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(ActiveSites)